// stripe.api.js
import axios from 'axios';

/**
 * Create Stripe Connect account and get onboarding link
 * @param {String} [country] - ISO 3166-1 alpha-2 code for a new account.
 *   Required when the user has no account yet; ignored once one exists,
 *   because Stripe fixes the country at creation.
 * @returns {Promise<Object>} { url, accountId, onboardingComplete, country }
 */
export async function createConnectAccount(country) {
  const response = await axios.post(
    '/api/stripe/create-connect-account',
    country ? { country } : {},
    {
      withCredentials: true,
    },
  );
  return response.data;
}

/**
 * Create a Stripe Connect account session for embedded onboarding
 * @param {String} [country] - ISO 3166-1 alpha-2 code for a new account.
 * @returns {Promise<Object>} { clientSecret, accountId, onboardingComplete, country }
 */
export async function createAccountSession(country) {
  const response = await axios.post(
    '/api/stripe/create-account-session',
    country ? { country } : {},
    {
      withCredentials: true,
    },
  );
  return response.data;
}

/**
 * Forget an unfinished Stripe Connect account so the user can start over,
 * for example to choose a different country. Refused once the account can
 * receive payments.
 * @returns {Promise<Object>} { success, previousAccountId, previousCountry }
 */
export async function resetConnectAccount() {
  const response = await axios.post(
    '/api/stripe/reset-connect-account',
    {},
    {
      withCredentials: true,
    },
  );
  return response.data;
}

/**
 * Verify onboarding completion after returning from Stripe
 * @param {String} accountId - Optional account ID from query params
 * @returns {Promise<Object>} Account status and onboarding info
 */
export async function verifyOnboarding(accountId = null) {
  const params = accountId ? { account: accountId } : {};
  const response = await axios.get('/api/stripe/verify-onboarding', {
    params,
    withCredentials: true,
  });
  return response.data;
}

/**
 * Get Stripe Connect account status
 * @returns {Promise<Object>} Account status information
 */
export async function getAccountStatus() {
  const response = await axios.get('/api/stripe/account-status', {
    withCredentials: true,
  });
  return response.data;
}

/**
 * Activate payments for user's Stripe account
 * @returns {Promise<Object>} { success, paymentsActive, message }
 */
export async function activatePayments() {
  const response = await axios.post(
    '/api/stripe/activate-payments',
    {},
    {
      withCredentials: true,
    },
  );
  return response.data;
}

/**
 * Deactivate payments for user's Stripe account
 * @returns {Promise<Object>} { success, paymentsActive, message }
 */
export async function deactivatePayments() {
  const response = await axios.post(
    '/api/stripe/deactivate-payments',
    {},
    {
      withCredentials: true,
    },
  );
  return response.data;
}
